/**
 * People on a property, with a named role (punch 4.5; docs/14 Q-5; docs/11 X-8
 * part 2, D-4).
 *
 * A DIRECTORY fact — who to call about this building — and deliberately not an
 * access fact.
 *
 * NOTHING HERE REPORTS WHETHER A PERSON HAS A LOGIN (mailbox RM-035).
 * `users_self_read` (0003) lets a non-administrator see only themselves in
 * `users`, so a property manager reading this directory could not tell a
 * colleague's account status apart from its absence — and a field that is
 * sometimes false for the wrong reason is worse than no field. Identity and
 * access problems are reported by the scoped reconciliation, which runs as a
 * self-authorizing SECURITY DEFINER read and can see what it is talking about. `property_assignments` remains the only thing access is
 * materialized from, and nothing in this contract can widen it. The role
 * vocabulary is admin-managed reference data that grants nothing.
 *
 * The reconciliation is the other half of Matt's answer to Q-5: rather than
 * forcing the two lists into step, the app REPORTS where they disagree, because
 * somebody is legitimately mid-handover for a week. It is a read, it warns, and
 * it never blocks a write.
 */

import { z } from 'zod';

/** One person, in one role, on one property. */
export const propertyPersonWriteSchema = z.object({
  internalContactId: z.uuid(),
  propertyRoleId: z.uuid(),
});
export type PropertyPersonWrite = z.infer<typeof propertyPersonWriteSchema>;

/**
 * The whole list, replaced in one write, carrying the PROPERTY's version.
 *
 * A full-set replacement with a parent-version guard, which is the contract
 * punch 4.4 established for exactly this shape (docs/11 F-49): the links have no
 * version of their own, so the token comes from the property and the write bumps
 * it. Who is listed on a building is part of what the building record says.
 */
export const propertyPeopleWriteSchema = z.object({
  version: z.number().int().positive(),
  people: z.array(propertyPersonWriteSchema).max(60),
});
export type PropertyPeopleWrite = z.infer<typeof propertyPeopleWriteSchema>;

export interface PropertyPersonRow {
  readonly id: string;
  readonly internalContactId: string;
  readonly contactName: string;
  readonly contactEmail: string | null;
  readonly contactTitle: string | null;
  readonly propertyRoleId: string;
  readonly roleName: string;
  /** Whether this role EXPECTS a direct property assignment. Grants nothing. */
  readonly requiresDirectAssignment: boolean;
  /**
   * Whether the role and the contact are still ACTIVE reference data (mailbox
   * RM-040). A listing can outlive either: a role deactivated while its
   * building was inactive comes back live with the building, and nothing at all
   * guards deactivating a person who is listed somewhere.
   *
   * The editor needs to tell those rows apart, because the rule for them is
   * "keep exactly as-is, or remove" — they cannot be re-chosen, and a picker
   * that simply omitted them would render the row blank and quietly drop a
   * listing the moment somebody edited anybody else.
   */
  readonly roleActive: boolean;
  readonly contactActive: boolean;
}

/**
 * The three drift conditions, reported separately because they need different
 * actions from a person (Matt, 2026-08-31).
 *
 * - `has_access_not_listed` — somebody can write to this property and appears in
 *   its directory under NO ACTIVE ROLE AT ALL. Not "no role that expects an
 *   assignment" (mailbox RM-036): `requires_direct_assignment = false` means an
 *   assignment is not REQUIRED, never that it is forbidden, so an assigned
 *   Accountant visibly listed as Accountant is listed and must not be reported.
 *   Either the directory is behind, or the access should not be there.
 * - `listed_no_assignment` — listed in a role that expects an assignment, has an
 *   active PropOS login, and holds no direct assignment. They cannot do the job
 *   the directory says they have.
 * - `listed_no_user` — listed in such a role with no active linked login at all.
 *   NOT silently excluded: a Maintenance Manager with no way into the app is a
 *   real gap, and hiding it would be the app deciding it knows better.
 *
 * A role that does NOT expect an assignment never produces any of these.
 */
/** An option an editor may CHOOSE. Inactive roles are excluded by construction:
 *  a deactivated role keeps naming itself on existing links (history stays
 *  readable) and disappears from new selection (docs/02 invariant 9). */
export interface PropertyRoleOption {
  readonly id: string;
  readonly name: string;
  readonly requiresDirectAssignment: boolean;
}

export interface PropertyPersonOption {
  readonly id: string;
  readonly name: string;
  readonly title: string | null;
}

/**
 * One request serves the whole card: who is listed, plus what an editor may
 * choose from. Bundled rather than split so the picker cannot render before its
 * options arrive, and so the card needs no permission beyond the one that got
 * it here.
 */
export interface PropertyPeopleResponse {
  readonly rows: readonly PropertyPersonRow[];
  readonly roleOptions: readonly PropertyRoleOption[];
  readonly contactOptions: readonly PropertyPersonOption[];
}

export type PropertyPeopleDriftKind =
  'has_access_not_listed' | 'listed_no_assignment' | 'listed_no_user';

export interface PropertyPeopleDriftRow {
  readonly kind: PropertyPeopleDriftKind;
  readonly propertyId: string;
  readonly propertyName: string;
  /** The person, named. Null contact id on `has_access_not_listed`. */
  readonly internalContactId: string | null;
  readonly personName: string;
  /** The role the expectation comes from; null when the drift is access-side. */
  readonly propertyRoleId: string | null;
  readonly roleName: string | null;
}
